import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { HeaderPageActions } from '../HeaderPageActions';
import { RotatingDeleteIcon } from '../RotatingDeleteIcon';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_FILTER_CONTROL_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  AdminErrorAlert,
  AdminLoadingState,
  AdminEmptyState,
  AdminTableWrap,
} from './admin-ui';

type FeatureFlagEveryone = 'no_one' | 'everyone' | null;

interface FeatureFlagUser {
  id: string;
  email: string;
}

interface FeatureFlag {
  key: string;
  everyone: FeatureFlagEveryone;
  users: FeatureFlagUser[];
  createdAt: string;
  updatedAt: string;
}

interface AdminUser {
  id: string;
  email: string;
}

const everyoneOptions: Array<{ value: FeatureFlagEveryone; labelKey: string }> = [
  { value: null, labelKey: 'feature_flag_everyone_not_set' },
  { value: 'no_one', labelKey: 'feature_flag_everyone_no_one' },
  { value: 'everyone', labelKey: 'feature_flag_everyone_everyone' },
];

export const FeatureFlagsAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newEveryone, setNewEveryone] = useState<FeatureFlagEveryone>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editEveryone, setEditEveryone] = useState<FeatureFlagEveryone>(null);
  const [editUserIds, setEditUserIds] = useState<string[]>([]);
  const [editUsers, setEditUsers] = useState<FeatureFlagUser[]>([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const authHeaders = (): Record<string, string> => ({
    ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
  });

  const apiUrl = (path: string): string => {
    const base = `${API_BASE}/api/feature-flags${path}`;
    if (masterKey) {
      return `${base}${path.includes('?') ? '&' : '?'}masterKey=${encodeURIComponent(masterKey)}`;
    }
    return base;
  };

  const fetchFlags = async (): Promise<void> => {
    const response = await fetch(apiUrl(''), { headers: authHeaders() });
    if (!response.ok) {
      throw new Error('Failed to fetch feature flags');
    }
    const data = await response.json();
    setFlags(data.flags || []);
  };

  const fetchAdminUsers = async (): Promise<void> => {
    const url = masterKey
      ? `${API_BASE}/api/users?masterKey=${encodeURIComponent(masterKey)}`
      : `${API_BASE}/api/users`;
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      return;
    }
    const data = await response.json();
    setAdminUsers((data.users || []).map((u: AdminUser) => ({ id: u.id, email: u.email })));
  };

  useEffect(() => {
    const load = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        await Promise.all([fetchFlags(), fetchAdminUsers()]);
        setError(null);
      } catch (err) {
        console.error('Error fetching feature flags:', err);
        setError(t('failed_to_load_feature_flags'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, user, masterKey, t]);

  const handleCreate = async () => {
    if (!newKey.trim() || submitting) return;
    try {
      setSubmitting(true);
      const response = await fetch(apiUrl(''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          key: newKey.trim(),
          everyone: newEveryone,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create feature flag');
      }
      const data = await response.json();
      setFlags([data.flag, ...flags]);
      setNewKey('');
      setNewEveryone(null);
      setShowCreateForm(false);
      setError(null);
    } catch (err: unknown) {
      console.error('Error creating feature flag:', err);
      setError(err instanceof Error ? err.message : t('failed_to_create_feature_flag'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (key: string) => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const response = await fetch(apiUrl(`/${encodeURIComponent(key)}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          everyone: editEveryone,
          userIds: editUserIds,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update feature flag');
      }
      const data = await response.json();
      setFlags(flags.map((f) => (f.key === key ? data.flag : f)));
      setEditingKey(null);
      setError(null);
    } catch (err: unknown) {
      console.error('Error updating feature flag:', err);
      setError(err instanceof Error ? err.message : t('failed_to_update_feature_flag'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (submitting || !confirm(t('confirm_delete_feature_flag'))) return;
    try {
      setSubmitting(true);
      const response = await fetch(apiUrl(`/${encodeURIComponent(key)}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to delete feature flag');
      }
      setFlags(flags.filter((f) => f.key !== key));
      setError(null);
    } catch (err) {
      console.error('Error deleting feature flag:', err);
      setError(t('failed_to_delete_feature_flag'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (flag: FeatureFlag) => {
    setEditingKey(flag.key);
    setEditEveryone(flag.everyone);
    setEditUserIds(flag.users.map((u) => u.id));
    setEditUsers(flag.users);
    setNewUserEmail('');
  };

  const addUserByEmail = () => {
    const email = newUserEmail.trim().toLowerCase();
    if (!email) return;
    const matchedUser = adminUsers.find((u) => u.email.toLowerCase() === email);
    if (!matchedUser) {
      setError(t('feature_flag_user_not_found'));
      return;
    }
    if (editUserIds.includes(matchedUser.id)) {
      setNewUserEmail('');
      return;
    }
    setEditUserIds([...editUserIds, matchedUser.id]);
    setEditUsers([...editUsers, matchedUser]);
    setNewUserEmail('');
    setError(null);
  };

  const removeUser = (userId: string) => {
    setEditUserIds(editUserIds.filter((id) => id !== userId));
    setEditUsers(editUsers.filter((u) => u.id !== userId));
  };

  const renderEveryoneLabel = (value: FeatureFlagEveryone): string => {
    const option = everyoneOptions.find((o) => o.value === value);
    return option ? t(option.labelKey) : value ?? '';
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <HeaderPageActions>
        <Button type="button" variant={showCreateForm ? 'outline' : 'default'} onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? t('cancel') : t('create_feature_flag')}
        </Button>
      </HeaderPageActions>

      {error && <AdminErrorAlert>{error}</AdminErrorAlert>}

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>{t('create_feature_flag')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="feature-flag-key">{t('feature_flag_key')}</Label>
              <Input
                id="feature-flag-key"
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder={t('feature_flag_key_placeholder')}
                className="font-mono"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="feature-flag-everyone">{t('feature_flag_everyone')}</Label>
              <select
                id="feature-flag-everyone"
                value={newEveryone ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewEveryone(value === '' ? null : value as 'no_one' | 'everyone');
                }}
                className={ADMIN_FILTER_CONTROL_CLASSNAME}
              >
                {everyoneOptions.map((option) => (
                  <option key={option.labelKey} value={option.value ?? ''}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={submitting || !newKey.trim()}
            >
              {submitting ? t('creating') : t('create')}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <AdminLoadingState />
      ) : flags.length === 0 ? (
        <AdminEmptyState>{t('no_feature_flags')}</AdminEmptyState>
      ) : (
        <AdminTableWrap>
          <table className="admin-data-table min-w-[900px]">
            <thead>
              <tr>
                <th>{t('feature_flag_key')}</th>
                <th>{t('feature_flag_everyone')}</th>
                <th>{t('feature_flag_users')}</th>
                <th>{t('updated')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.key} className="align-top">
                  <td className="font-mono font-semibold">{flag.key}</td>
                  <td>
                    {editingKey === flag.key ? (
                      <select
                        value={editEveryone ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditEveryone(value === '' ? null : value as 'no_one' | 'everyone');
                        }}
                        className={ADMIN_FILTER_CONTROL_CLASSNAME}
                      >
                        {everyoneOptions.map((option) => (
                          <option key={option.labelKey} value={option.value ?? ''}>
                            {t(option.labelKey)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      renderEveryoneLabel(flag.everyone)
                    )}
                  </td>
                  <td>
                    {editingKey === flag.key ? (
                      <div className="flex flex-col gap-2">
                        <ul className="m-0 list-disc pl-5">
                          {editUsers.map((u) => (
                            <li key={u.id} className="mb-1">
                              {u.email}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeUser(u.id)}
                                className="ml-2 h-auto px-1.5 py-0 text-xs"
                              >
                                {t('remove')}
                              </Button>
                            </li>
                          ))}
                        </ul>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            placeholder={t('user_email')}
                            className="h-8 flex-1"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={addUserByEmail}
                          >
                            {t('add')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className={ADMIN_MUTED_TEXT_CLASSNAME}>
                        {flag.users.length === 0
                          ? t('none')
                          : flag.users.map((u) => u.email).join(', ')}
                      </span>
                    )}
                  </td>
                  <td className="admin-table-cell-muted">
                    {new Date(flag.updatedAt).toLocaleString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      {editingKey === flag.key ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleUpdate(flag.key)}
                            disabled={submitting}
                          >
                            {t('save')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingKey(null)}
                            disabled={submitting}
                          >
                            {t('cancel')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => startEdit(flag)}
                            disabled={submitting}
                          >
                            {t('edit')}
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(flag.key)}
                            disabled={submitting}
                            className="group inline-flex items-center gap-1.5"
                          >
                            <RotatingDeleteIcon />
                            {t('delete')}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableWrap>
      )}
    </div>
  );
};
